<template>
  <Loader
    :value="promises.fetchOne"
    :message="$t('loading', { resource: $tc('contract', 2) }) + '...'"
    class="contracts"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #center>
          <CheckboxComponent v-model="showDeclinedContracts" :label="$t('showDeclinedContracts')" />
        </template>
      </HeaderComponent>
      <DataTable
        v-if="items.length"
        sticky="var(--header-height)"
        :value="items"
        :columns="fields.map(c => ({ id: c.id, label: c.label }))"
        :row-wrapper="
          item => ({
            component: 'router-link',
            props: {
              to: { name: 'contract', params: { id: item.id } },
              style: 'color: inherit',
            },
          })
        "
      >
        <template v-slot:[column.id]="{ item }" v-for="column in fields">
          <component :is="column.component" v-bind="column.props(item)">
            {{ column.get(item) }}
          </component>
        </template>
      </DataTable>
      <EmptyState
        v-else-if="!errors.fetchOne"
        :title="$t('noDocuments')"
        :subtitle="$t('documentsEmptyState')"
        :icon="['fal', 'file']"
      />
    </div>
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { Capitalize, CamelCase, SortByKey } from '@/utils'
import { path } from 'ramda'
import { CheckboxComponent } from 'vue-elder-checkbox'

import HeaderComponent from '@/components/Header'
import Badge from '@/components/Badge'
import EmptyState from '@/components/EmptyState'
import DataTable from '@/components/DataTable'

const getState = key => {
  return state => {
    return path(key.split('.'), state)
  }
}

export default {
  watch: {
    filter: {
      handler: 'fetch',
      deep: true,
      immediate: true,
    },
  },
  props: {
    id: String,
  },
  data() {
    return {
      filter: {
        status: ['!draft', '!declined'],
      },
    }
  },
  computed: {
    ...mapState('Contract', {
      item: getState('item'),
      promises: getState('promises'),
      errors: getState('errors'),
      project(state) {
        switch (this.$route.name) {
          case 'project-contracts':
            return this.$path('item', state)
          case 'residential-contracts':
            return this.$path('item.project', state)
        }
      },
    }),
    showDeclinedContracts: {
      get() {
        return !this.filter.status.includes('!declined')
      },
      set(val) {
        if (val) this.filter.status = this.filter.status.filter(e => e !== '!declined')
        else this.filter.status.push('!declined')
      },
    },
    StatusMap() {
      return {
        draft: { value: 'default' },
        signed: { value: 'warning' },
        accepted: { value: 'success', icon: 'check-circle' },
        declined: { value: 'error' },
      }
    },
    items() {
      return [...(this.item.contracts || [])]
        .sort(SortByKey('type'))
        .filter(item => this.hasAccess(item))
        .filter(item => this.StatusMap[item.status])
    },
    breadcrumb() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$path('item.project.name') || this.item.name,
          action: { name: 'project-dashboard', params: { id: this.$path('item.project.id') || this.id } },
        },
        {
          title: this.item.name,
          condition: this.$route.name === 'residential-contracts',
        },
        { title: this.$tc('document', 2) },
      ].filter(f => !('condition' in f) || f.condition)
    },
    fields() {
      return [
        {
          id: 'type',
          label: this.$tc('document'),
          get: i => i.title,
          props: () => ({}),
          component: 'span',
        },
        {
          id: 'status',
          label: this.$tc('status'),
          props: i => {
            let status = this.StatusMap[i.status]
            if (!status) return
            return { theme: status.value, icon: status.icon }
          },
          get: i => this.$tc('contractStatus' + Capitalize(i.status)),
          component: Badge,
        },
      ].filter(f => !('condition' in f) || f.condition)
    },
  },
  methods: {
    ...mapActions('Contract', {
      fetch(dispatch) {
        return dispatch('fetch', [
          { id: this.id, filter: this.filter },
          { query: Capitalize(CamelCase(this.$route.name)) },
        ])
      },
    }),
    hasAccess(item) {
      switch (item.type) {
        case 'bid':
        case 'purchase':
          return this.$store.getters['Project/hasRole'](['signingAuthority', 'manager', 'salesman'], this.project)
        case 'shop':
        case 'shop-partial':
          return this.$store.getters['Project/hasRole'](/^shop/, this.project)
      }
    },
  },
  metaInfo() {
    switch (this.$route.name) {
      case 'project-contracts':
      case 'residential-contracts':
        return { title: `${this.$tc('document', 2)} - ${this.item.name}` }
      default:
        return { title: this.$tc('document', 2) }
    }
  },
  components: {
    HeaderComponent,
    EmptyState,
    Badge,
    DataTable,
    CheckboxComponent,
  },
}
</script>

<style lang="scss">
.contracts {
  background-color: $background-grey;

  &__content {
    min-height: 70vh;
  }

  .header {
    &__center {
      flex-grow: 1;
      display: flex;
      justify-content: flex-end;
    }
    &__right {
      flex-grow: 0;
    }
  }
}
</style>
